// ADM-1 (2026-06-12) — Ouvidoria ANÔNIMA.
// Denúncias, reclamações e sugestões. O sistema NÃO grava quem abriu
// (criado_por fica NULL no banco — garantido no backend). O acompanhamento
// é feito pelo protocolo, mostrado uma única vez na abertura. Os chamados
// caem no kanban do administrativo como os demais tickets.

import { useState } from 'react';
import axios from 'axios';
import {
  App as AntApp, Alert, Button, Card, Divider, Flex, Form, Input, Radio,
  Space, Tag, Typography,
} from 'antd';
import { SearchOutlined, SendOutlined, CopyOutlined } from '@ant-design/icons';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
import { fmtDataBr } from '../utils/dataFmt';
import { TICKET_STATUS_CFG } from './AtividadesRequisicoes';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

const SUBTIPO_CFG: Record<string, { label: string; emoji: string; placeholder: string }> = {
  denuncia:   { label: 'Denúncia',   emoji: '🚨', placeholder: 'Descreva a situação. Quanto mais detalhes (datas, locais, contexto), melhor a apuração — sem se identificar.' },
  reclamacao: { label: 'Reclamação', emoji: '😤', placeholder: 'O que está incomodando? Descreva a situação e, se quiser, sugira como resolver.' },
  sugestao:   { label: 'Sugestão',   emoji: '💡', placeholder: 'Qual a sua ideia pra melhorar a empresa, o escritório ou os processos?' },
};

function AtividadesOuvidoriaInner() {
  const { message } = AntApp.useApp();
  const [subtipo, setSubtipo] = useState<'denuncia' | 'reclamacao' | 'sugestao'>('sugestao');
  const [enviando, setEnviando] = useState(false);
  const [protocoloGerado, setProtocoloGerado] = useState<string | null>(null);
  const [form] = Form.useForm();
  // Consulta por protocolo
  const [protocoloBusca, setProtocoloBusca] = useState('');
  const [consultando, setConsultando] = useState(false);
  const [consulta, setConsulta] = useState<any | null>(null);
  const [resposta, setResposta] = useState('');
  const [respondendo, setRespondendo] = useState(false);

  async function enviar(values: any) {
    setEnviando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_ticket_criar',
          tipo: 'ouvidoria',
          subtipo,
          anonimo: true,
          titulo: values.titulo,
          descricao: values.descricao,
        }, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setProtocoloGerado(d.protocolo);
      form.resetFields();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally { setEnviando(false); }
  }

  async function consultar() {
    if (!protocoloBusca.trim()) return;
    setConsultando(true);
    setConsulta(null);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_ticket_por_protocolo', protocolo: protocoloBusca.trim() },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setConsulta(d);
    } catch (e: any) {
      message.error(e.response?.status === 404 ? 'Protocolo não encontrado'
        : 'Falha: ' + (e.response?.data?.error || e.message));
    } finally { setConsultando(false); }
  }

  async function responder() {
    if (!resposta.trim() || !consulta) return;
    setRespondendo(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_ticket_comentar', protocolo: consulta.protocolo, texto: resposta.trim() },
        { headers: { Authorization: token } });
      message.success('Mensagem enviada (anônima)');
      setResposta('');
      await consultar();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally { setRespondendo(false); }
  }

  return (
    <div style={{ padding: 16, maxWidth: 760 }}>
      <Title level={4} style={{ margin: 0 }}>📢 Ouvidoria</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 14, fontSize: 12 }}>
        Canal <strong>anônimo</strong> pra denúncias, reclamações e sugestões.
      </Text>

      <Alert type="info" showIcon style={{ marginBottom: 16 }}
        message="Como funciona o anonimato"
        description={
          <Text style={{ fontSize: 12 }}>
            O sistema <strong>não registra quem abriu</strong> o chamado — nem nome, nem email.
            Ao enviar, você recebe um <strong>protocolo</strong>: guarde-o, é a única forma de
            acompanhar a resposta do administrativo (ele aparece só uma vez).
          </Text>
        } />

      {protocoloGerado ? (
        <Card style={{ marginBottom: 16, borderColor: '#389e0d', background: '#F6FFED' }}>
          <Title level={5} style={{ marginTop: 0 }}>✅ Chamado enviado!</Title>
          <Paragraph style={{ fontSize: 13 }}>
            Guarde o protocolo abaixo — <strong>ele não será mostrado de novo</strong>:
          </Paragraph>
          <Flex align="center" gap={8}>
            <Text code copyable style={{ fontSize: 22, fontWeight: 700 }}>{protocoloGerado}</Text>
            <Button size="small" icon={<CopyOutlined />}
              onClick={() => { navigator.clipboard?.writeText(protocoloGerado); message.success('Copiado'); }}>
              Copiar
            </Button>
          </Flex>
          <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 10, marginBottom: 0 }}>
            Use o campo "Consultar protocolo" abaixo pra ver a resposta e conversar
            com o administrativo sem se identificar.
          </Paragraph>
          <Button type="link" size="small" style={{ paddingLeft: 0, marginTop: 6 }}
            onClick={() => setProtocoloGerado(null)}>
            Abrir outro chamado
          </Button>
        </Card>
      ) : (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Radio.Group value={subtipo} onChange={e => setSubtipo(e.target.value)}
            buttonStyle="solid" style={{ marginBottom: 14 }}>
            {Object.entries(SUBTIPO_CFG).map(([k, v]) => (
              <Radio.Button key={k} value={k}>{v.emoji} {v.label}</Radio.Button>
            ))}
          </Radio.Group>
          <Form form={form} layout="vertical" size="small" onFinish={enviar}>
            <Form.Item label="Assunto" name="titulo"
              rules={[{ required: true, message: 'Resuma em uma linha' }]}>
              <Input placeholder="Resumo em uma linha" maxLength={200} />
            </Form.Item>
            <Form.Item label="Descrição" name="descricao"
              rules={[{ required: true, min: 20, message: 'Descreva com pelo menos 20 caracteres' }]}>
              <TextArea rows={6} placeholder={SUBTIPO_CFG[subtipo].placeholder} />
            </Form.Item>
            <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={enviando} block>
              Enviar anonimamente
            </Button>
          </Form>
        </Card>
      )}

      <Divider />

      {/* ─── Consulta por protocolo ─── */}
      <Title level={5}>🔎 Consultar protocolo</Title>
      <Flex gap={8} style={{ marginBottom: 12, maxWidth: 380 }}>
        <Input value={protocoloBusca} onChange={e => setProtocoloBusca(e.target.value)}
          placeholder="Ex: T-4F8A2C" onPressEnter={consultar} />
        <Button icon={<SearchOutlined />} loading={consultando} onClick={consultar}>Consultar</Button>
      </Flex>

      {consulta && (
        <Card size="small">
          <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
            <Text strong style={{ fontSize: 13 }}>
              {consulta.protocolo} — {consulta.titulo}
            </Text>
            <Tag color={TICKET_STATUS_CFG[consulta.status]?.cor}>
              {TICKET_STATUS_CFG[consulta.status]?.label || consulta.status}
            </Tag>
          </Flex>
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 10 }}>
            Aberto em {fmtDataBr(consulta.criado_em)}
            {consulta.concluido_em && ` · concluído em ${fmtDataBr(consulta.concluido_em)}`}
          </Text>

          {(consulta.comentarios || []).length === 0 ? (
            <Text type="secondary" italic style={{ fontSize: 12 }}>
              Ainda sem resposta do administrativo.
            </Text>
          ) : (
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              {consulta.comentarios.map((c: any) => (
                <Card key={c.id} size="small"
                  style={{ background: c.do_solicitante ? '#F0F5FF' : '#F6FFED' }}
                  bodyStyle={{ padding: '6px 10px' }}>
                  <Flex justify="space-between" gap={8}>
                    <Text strong style={{ fontSize: 11 }}>
                      {c.do_solicitante ? '🕵️ Você (anônimo)' : `${c.autor_nome || 'Administrativo'}`}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 10 }}>{fmtDataBr(c.criado_em)}</Text>
                  </Flex>
                  <Text style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{c.texto}</Text>
                </Card>
              ))}
            </Space>
          )}

          <Flex gap={6} style={{ marginTop: 10 }}>
            <TextArea rows={2} value={resposta} onChange={e => setResposta(e.target.value)}
              placeholder="Responder anonimamente…" style={{ flex: 1 }} />
            <Button type="primary" icon={<SendOutlined />} loading={respondendo}
              onClick={responder} disabled={!resposta.trim()} />
          </Flex>
        </Card>
      )}
    </div>
  );
}

export default function AtividadesOuvidoria() {
  return <AntApp><AtividadesOuvidoriaInner /></AntApp>;
}
